/**
 * ServicesSection - Service selection and customization
 *
 * Handles quoteServices array - identical data flow to quoteCustomParts
 */

import React, { useState, useCallback, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { SelectedServicesList } from "@/components/services/SelectedServicesList";
import { QuoteFormData, TemplateFormData } from "../index";
import { useServices } from "@/hooks/use-services";
import type { QuoteService } from "@onsemble/shared";

interface ServicesSectionProps {
  form: UseFormReturn<QuoteFormData | TemplateFormData>;
  organizationId: string;
}

export const ServicesSection: React.FC<ServicesSectionProps> = ({
  form,
  organizationId,
}) => {
  const [isAddingService, setIsAddingService] = useState(false);
  const { services } = useServices();

  // Convert quoteServices to legacy format for SelectedServicesList
  const { selectedServices, serviceCustomPrices, serviceCustomNames } =
    useMemo(() => {
      const quoteServices = (form.watch("quoteServices") ||
        []) as QuoteService[];
      const selected: string[] = [];
      const prices: Record<string, number> = {};
      const names: Record<string, string> = {};

      quoteServices.forEach((qs) => {
        if (qs.id) {
          selected.push(qs.id);
          if (qs.price !== undefined && qs.price !== null) {
            prices[qs.id] = qs.price;
          }
          if (qs.name) {
            names[qs.id] = qs.name;
          }
        }
      });

      return {
        selectedServices: selected,
        serviceCustomPrices: prices,
        serviceCustomNames: names,
      };
    }, [form.watch("quoteServices")]);

  // Convert legacy format back to quoteServices
  const handleServicesChange = useCallback(
    (
      serviceIds: string[],
      prices?: Record<string, number>,
      names?: Record<string, string>,
    ) => {
      const currentQuoteServices = (form.watch("quoteServices") ||
        []) as QuoteService[];
      const newQuoteServices: QuoteService[] = serviceIds.map((serviceId) => {
        const existing = currentQuoteServices.find((qs) => qs.id === serviceId);
        const serviceDetails = services?.find((s) => s.uid === serviceId);

        return {
          id: serviceId,
          name:
            names && serviceId in names
              ? names[serviceId]
              : (existing?.name ?? serviceDetails?.title),
          price:
            prices && serviceId in prices
              ? prices[serviceId]
              : existing?.price !== undefined && existing?.price !== null
                ? existing.price
                : serviceDetails?.cost,
        };
      });

      form.setValue("quoteServices", newQuoteServices, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [form, services],
  );

  const handleSelectedServicesChange = useCallback(
    (serviceIds: string[]) => {
      // BaseItemSelector calls setSelectedItems, setCustomPrices, and setCustomNames separately
      // We need to merge all three updates. Since the prices/names handlers will be called next,
      // we'll just update the service IDs here and let those handlers update prices/names
      // But we need to ensure new services get their catalog price/name
      const currentQuoteServices = (form.watch("quoteServices") ||
        []) as QuoteService[];
      const currentPrices: Record<string, number> = {};
      const currentNames: Record<string, string> = {};

      // Get existing prices/names from current quoteServices
      currentQuoteServices.forEach((qs) => {
        if (qs.id && serviceIds.includes(qs.id)) {
          if (qs.price !== undefined && qs.price !== null) {
            currentPrices[qs.id] = qs.price;
          }
          if (qs.name) {
            currentNames[qs.id] = qs.name;
          }
        }
      });

      // For new services not in currentQuoteServices, get from catalog
      serviceIds.forEach((serviceId) => {
        if (!currentQuoteServices.find((qs) => qs.id === serviceId)) {
          const serviceDetails = services?.find((s) => s.uid === serviceId);
          if (serviceDetails) {
            currentPrices[serviceId] = serviceDetails.cost || 0;
            currentNames[serviceId] = serviceDetails.title || "";
          }
        }
      });

      handleServicesChange(serviceIds, currentPrices, currentNames);
    },
    [form, handleServicesChange, services],
  );

  const handlePricesChange = useCallback(
    (prices: Record<string, number>) => {
      const currentQuoteServices = (form.watch("quoteServices") ||
        []) as QuoteService[];
      const currentSelected = currentQuoteServices
        .map((qs) => qs.id)
        .filter(Boolean) as string[];
      const currentNames: Record<string, string> = {};

      currentQuoteServices.forEach((qs) => {
        if (qs.id && currentSelected.includes(qs.id) && qs.name) {
          currentNames[qs.id] = qs.name;
        }
      });

      handleServicesChange(currentSelected, prices, currentNames);
    },
    [form, handleServicesChange, services],
  );

  const handleNamesChange = useCallback(
    (names: Record<string, string>) => {
      const currentQuoteServices = (form.watch("quoteServices") ||
        []) as QuoteService[];
      const currentSelected = currentQuoteServices
        .map((qs) => qs.id)
        .filter(Boolean) as string[];
      const currentPrices: Record<string, number> = {};

      currentQuoteServices.forEach((qs) => {
        if (
          qs.id &&
          currentSelected.includes(qs.id) &&
          qs.price !== undefined &&
          qs.price !== null
        ) {
          currentPrices[qs.id] = qs.price;
        }
      });

      handleServicesChange(currentSelected, currentPrices, names);
    },
    [form, handleServicesChange, services],
  );

  return (
    <div className="border-t pt-6">
      <SelectedServicesList
        orgId={organizationId}
        selectedServices={selectedServices}
        setSelectedServices={handleSelectedServicesChange}
        serviceCustomPrices={serviceCustomPrices}
        setServiceCustomPrices={handlePricesChange}
        serviceCustomNames={serviceCustomNames}
        setServiceCustomNames={handleNamesChange}
        isAddingService={isAddingService}
        setIsAddingService={setIsAddingService}
      />
    </div>
  );
};
